import classNames from "classnames";
import { useState } from "react";
import { useFilterManager, FilterManagerWrapper } from "@ugg/shared/components/FilterManager/FilterManager";
import { PatchOption } from "@ugg/shared/interfaces/filter-manager.interface";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { getValidParamKeys } from "@ugg/shared/query-params/params-helpers";
import { MediaQuery, useResponsive } from "@outplayed/responsive";
import { ReactComponent as FilterIcon } from "@ugg/shared/assets/svg/filters-icon.svg";
import { TIER_LIST_PAGES, useTierListPage } from "@ugg/shared/pages/tier-list-pages";
import { TIER_LIST_ROLE_OPTIONS } from "@ugg/shared/query-params/filter-options/tier-lists/tier-list";

import { usePatchOptions } from "@ugg/shared/components/FilterManager/filters/usePatchOptions";
import Role from "@ugg/shared/components/FilterManager/filters/Role";
import RoleSplit from "@ugg/shared/components/FilterManager/filters/RoleSplit";
import TierListRole from "@ugg/shared/components/FilterManager/filters/TierListRole";
import TierListRoleSplit from "@ugg/shared/components/FilterManager/filters/TierListRoleSplit";
import Rank from "@ugg/shared/components/FilterManager/filters/Rank";
import QueueType from "@ugg/shared/components/FilterManager/filters/QueueType";
import Region from "@ugg/shared/components/FilterManager/filters/Region";
import Patch from "@ugg/shared/components/FilterManager/filters/Patch";
import DuoRole from "@ugg/shared/components/FilterManager/filters/DuoRole";
import ShowAll from "@ugg/shared/components/FilterManager/filters/ShowAll";
import FilterSearch from "@ugg/shared/components/FilterManager/filters/FilterSearch";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { MoreLessButton } from "../FilterManager/filters/MoreLessButton";

interface TierListFilterManagerProps {
  className?: string;
}

export const TierListFilterManager: React.FC<TierListFilterManagerProps> = ({ className }) => {
  const page = useTierListPage();
  const filterManagerRes = useFilterManager(page, validParams);
  const { filters } = filterManagerRes;

  const validParamKeys = getValidParamKeys(validParams, page);
  // Same guard as TierListHeader's: `page` is undefined for any URL that reaches
  // a tier list through RouterPaths.TIER_LIST's "/lol/:gameMode-tier-list"
  // catch-all without matching a real page. Indexing validParams with that gives
  // undefined, and the `in` check below then throws during renderToString, which
  // takes down the whole Express process rather than failing one request.
  // `default` is spelled out because usePatchOptions reads options.default.patch.
  const options = validParams[page] || ({ default: {} } as (typeof validParams)[keyof typeof validParams]);
  const patchFilterOption = ("patch" in options ? options.patch : { options: "latest", max: 5 }) as PatchOption;
  const patchOptions = usePatchOptions(patchFilterOption, filters.patch, options.default.patch);
  const [showAll, setShowAll] = useState(false);
  const { currentBreakpoint } = useResponsive();
  const isMobile = currentBreakpoint?.includes("MOBILE");

  const isMainTierList = [
    TIER_LIST_PAGES.DEFAULT_TIER_LIST,
    TIER_LIST_PAGES.TOP_TIER_LIST,
    TIER_LIST_PAGES.JUNGLE_TIER_LIST,
    TIER_LIST_PAGES.MID_TIER_LIST,
    TIER_LIST_PAGES.ADC_TIER_LIST,
    TIER_LIST_PAGES.SUPPORT_TIER_LIST,
  ].some((el) => el === page);

  const hasRank = [QueueTypeS.RANKED_SOLO, QueueTypeS.RANKED_FLEX].includes(filters.queueType);
  const hasRole = [
    QueueTypeS.RANKED_SOLO,
    QueueTypeS.RANKED_FLEX,
    QueueTypeS.NORMAL_DRAFT,
    QueueTypeS.QUICKPLAY,
    QueueTypeS.SWIFTPLAY,
    QueueTypeS.NEXUS_BLITZ,
    QueueTypeS.CLASSIC,
  ].includes(filters.queueType);

  const showMoreLessButton = isMainTierList && process.env.UGG_PUBLIC_PLATFORM !== "app";

  // Temp classname. Fix later
  return (
    <FilterManagerWrapper className={classNames("filter-manager_tier-list filter-manager", className)}>
      <div className="filter-manager_label">
        <FilterIcon />
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span>Filters</span>
        </MediaQuery>
      </div>
      <div className="flex-1 flex items-center flex-wrap gap-[12px]">
        {hasRole && !isMainTierList && validParamKeys.role && (
          <>
            <RoleSplit {...filterManagerRes} options={options.role ?? []} />
          </>
        )}
        {hasRole && isMainTierList && (
          <>
            <TierListRoleSplit {...filterManagerRes} defaultOption={TIER_LIST_ROLE_OPTIONS[0]} />
          </>
        )}
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          {validParamKeys.duo && <DuoRole {...filterManagerRes} options={options.duo ?? []} />}
          {validParamKeys.rank && hasRank && <Rank {...filterManagerRes} options={options.rank ?? []} />}
          {validParamKeys.queueType && (
            <QueueType {...filterManagerRes} options={"queueType" in options ? options.queueType : []} />
          )}
          {validParamKeys.patch && (
            <Patch {...filterManagerRes} options={patchOptions.options} isDefaultPatch={patchOptions.isDefaultPatch} />
          )}
          {validParamKeys.region && <Region {...filterManagerRes} options={options.region ?? []} />}
          {validParamKeys.allChampions && !isMobile && <ShowAll {...filterManagerRes} />}
        </MediaQuery>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE">
          {(showAll || !showMoreLessButton) && (
            <div className="contents">
              {validParamKeys.duo && <DuoRole {...filterManagerRes} options={options.duo ?? []} />}
              {validParamKeys.rank && hasRank && <Rank {...filterManagerRes} options={options.rank ?? []} />}
              {validParamKeys.queueType && (
                <QueueType {...filterManagerRes} options={"queueType" in options ? options.queueType : []} />
              )}
              {validParamKeys.patch && (
                <Patch {...filterManagerRes} options={patchOptions.options} isDefaultPatch={patchOptions.isDefaultPatch} />
              )}
              {validParamKeys.region && <Region {...filterManagerRes} options={options.region ?? []} />}
              {validParamKeys.allChampions && isMobile && <ShowAll {...filterManagerRes} />}
            </div>
          )}
          {showMoreLessButton && <MoreLessButton collapsed={!showAll} onClick={() => setShowAll(!showAll)} />}
        </MediaQuery>
        {/* Custom-mode filter, last in the wrap group so it flows onto a
            fitting row alongside the rest. Outside both breakpoint blocks so
            it shows on mobile too, rather than hiding behind More/Less. Opt-in
            per page through valid-params: `search` on the Classic and ARAM
            Mayhem champion tier lists. */}
        {validParamKeys.search && <FilterSearch {...filterManagerRes} placeholder="Search Champion" />}
      </div>
    </FilterManagerWrapper>
  );
};
